import React, { useState } from 'react'
import { Button, Card, DatePicker, message, Space, Typography } from 'antd'
import moment from 'moment'
import { ClientProfile } from 'types'
import convertTrialToLive from 'api/clients/convertTrialToLive'
import convertToTrial from 'api/clients/convertToTrial'
import extendTrial from 'api/clients/extendTrial'

const { Text } = Typography

interface TrialManagementProps {
	clientProfile: ClientProfile | null
	onSuccess: () => void
}

const TrialManagement: React.FC<TrialManagementProps> = ({
	clientProfile,
	onSuccess,
}) => {
	const [converting, setConverting] = useState(false)
	const [extending, setExtending] = useState(false)
	const [convertingToTrial, setConvertingToTrial] = useState(false)
	const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(
		null
	)
	const [trialEndForLive, setTrialEndForLive] = useState<moment.Moment | null>(
		null
	)

	const isDemo = !!clientProfile && clientProfile.platformMode === 'DEMO'

	if (!clientProfile) {
		return (
			<Card title='Trial management'>
				<Text type='secondary'>Loading client…</Text>
			</Card>
		)
	}

	if (!isDemo) {
		const handleConvertToTrial = async () => {
			if (!clientProfile?._id || !trialEndForLive) {
				message.warning('Please select a trial end date')
				return
			}
			const ts = trialEndForLive.endOf('day').valueOf()
			if (ts <= Date.now()) {
				message.warning('Trial end date must be in the future')
				return
			}
			try {
				setConvertingToTrial(true)
				await convertToTrial(clientProfile._id, ts)
				message.success('Client converted to trial mode')
				setTrialEndForLive(null)
				onSuccess()
			} finally {
				setConvertingToTrial(false)
			}
		}
		return (
			<Card title='Trial management'>
				<Space direction='vertical' size='middle' style={{ width: '100%' }}>
					<Text>
						Convert this Live client back to Trial mode and set a trial end date.
					</Text>
					<Space wrap align='end'>
						<div>
							<Text strong style={{ display: 'block', marginBottom: 4 }}>
								Trial end date
							</Text>
							<DatePicker
								value={trialEndForLive}
								onChange={d => setTrialEndForLive(d)}
								disabledDate={d => d && d.endOf('day').valueOf() <= Date.now()}
								format='DD MMM YYYY'
							/>
						</div>
						<Button
							type='primary'
							onClick={handleConvertToTrial}
							loading={convertingToTrial}
							disabled={!trialEndForLive}
						>
							Convert to Trial
						</Button>
					</Space>
				</Space>
			</Card>
		)
	}

	const trialEndTs = clientProfile.demoExpirationTimestamp
	const trialEndFormatted = trialEndTs
		? moment(trialEndTs).format('DD MMM YYYY')
		: '—'

	const handleConvertToLive = async () => {
		if (!clientProfile?._id) return
		try {
			setConverting(true)
			await convertTrialToLive(clientProfile._id)
			message.success('Client converted to live mode')
			onSuccess()
		} finally {
			setConverting(false)
		}
	}

	const handleExtendTrial = async () => {
		if (!clientProfile?._id || !selectedEndDate) {
			message.warning('Please select an end date')
			return
		}
		const ts = selectedEndDate.endOf('day').valueOf()
		if (ts <= Date.now()) {
			message.warning('Trial end date must be in the future')
			return
		}
		try {
			setExtending(true)
			await extendTrial(clientProfile._id, ts)
			message.success('Trial extended successfully')
			setSelectedEndDate(null)
			onSuccess()
		} finally {
			setExtending(false)
		}
	}

	return (
		<Card title='Trial management'>
			<Space direction='vertical' size='middle' style={{ width: '100%' }}>
				<div>
					<Text strong>Current trial ends: </Text>
					<Text>{trialEndFormatted}</Text>
				</div>
				<Space wrap>
					<Button type='primary' onClick={handleConvertToLive} loading={converting}>
						Convert to Live
					</Button>
				</Space>
				<Space wrap align='end'>
					<div>
						<Text strong style={{ display: 'block', marginBottom: 4 }}>
							Extend trial (new end date)
						</Text>
						<DatePicker
							value={selectedEndDate}
							onChange={d => setSelectedEndDate(d)}
							disabledDate={d => d && d.endOf('day').valueOf() <= Date.now()}
							format='DD MMM YYYY'
						/>
					</div>
					<Button
						type='default'
						onClick={handleExtendTrial}
						loading={extending}
						disabled={!selectedEndDate}
					>
						Extend trial
					</Button>
				</Space>
			</Space>
		</Card>
	)
}

export default TrialManagement
